import type { ReactNode } from 'react';

export type ListSize = 'sm' | 'md' | 'lg';

interface ListCompound {
  (props: ListProps): ReactNode;
  Item: typeof ListItem;
}

export interface ListItemProps {
  /** Primary label of the row. */
  title: ReactNode;
  /** Secondary line under the title. */
  description?: ReactNode;
  /** Leading slot — avatar, icon, or any ReactNode. */
  leading?: ReactNode;
  /** Trailing slot — action button, badge, meta. */
  trailing?: ReactNode;
  /** Click handler — when set, the row becomes a button role. */
  onClick?: (event: React.MouseEvent) => void;
  /** Marks the item as the currently active row. */
  active?: boolean;
  disabled?: boolean;
  className?: string;
}

export interface ListProps {
  size?: ListSize;
  /** Landmark label. */
  'aria-label'?: string;
  /** Add separators between items. Default true. */
  bordered?: boolean;
  children?: ReactNode;
  className?: string;
}

/** Vertical list of rows with leading / title + description / trailing
 *  slots. Simpler than Table — no columns, no sorting, no selection.
 *  Use for: file lists, activity feeds, search results, inbox rows.
 *
 *  The landmark role is `list` (implicit on `<ul>`), items are `listitem`.
 *  Interactive items become buttons so Enter/Space activate them. */
function ListRoot(props: ListProps): ReactNode {
  const { size = 'md', bordered = true, children, className, 'aria-label': ariaLabel } = props;
  const classes = ['vq-list', `vq-list--${size}`, bordered && 'vq-list--bordered', className]
    .filter(Boolean)
    .join(' ');
  return (
    <ul className={classes} aria-label={ariaLabel} data-component="List">
      {children}
    </ul>
  );
}

function ListItem(props: ListItemProps): ReactNode {
  const { title, description, leading, trailing, onClick, active, disabled, className } = props;
  const interactive = onClick !== undefined;
  const classes = [
    'vq-list__item',
    active === true && 'vq-list__item--active',
    disabled === true && 'vq-list__item--disabled',
    interactive && 'vq-list__item--interactive',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const body = (
    <>
      {leading !== undefined && <span className="vq-list__leading">{leading}</span>}
      <span className="vq-list__text">
        <span className="vq-list__title">{title}</span>
        {description !== undefined && <span className="vq-list__desc">{description}</span>}
      </span>
      {trailing !== undefined && <span className="vq-list__trailing">{trailing}</span>}
    </>
  );

  if (interactive) {
    return (
      <li>
        <button
          type="button"
          className={classes}
          onClick={onClick}
          disabled={disabled}
          aria-current={active === true ? 'true' : undefined}
        >
          {body}
        </button>
      </li>
    );
  }
  return <li className={classes}>{body}</li>;
}

export const List: ListCompound = Object.assign(ListRoot, { Item: ListItem });
